import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { isBuilderPlan, isFlexPlan } from '@/services/auth/plan-slugs';
import { hasGrantStarted, spendableGrantOriginalTotal, spendableGrantTotal } from '@/services/billing/credit-grants';

function toCents(dollars: number) {
  return Math.round((dollars + Number.EPSILON) * 100);
}

/**
 * Fetches the builder credit balance for the current account.
 * `credits_remaining` values are dollar amounts (e.g. 42.57 = $42.57).
 *
 * Reports `totalCents` (funds loaded) and `usedCents` (funds spent) beside the remaining balance, so
 * a usage meter fills from the same grants the balance reads.
 *
 * Also loads for the Flex plan, which uses the same wallet. `isBuilder` is true only for Builder.
 */
export function useBuilderBalance() {
  const { account } = useAuth();
  const isBuilder = isBuilderPlan(account);
  const hasWallet = isBuilder || isFlexPlan(account);

  const { data, isLoading, isError } = useHydraUIAPIQuery({
    queryKey: ['getBuilderCredits', { params: { account_id: account.id } }],
    queryFn: ({ signal }) =>
      hydraUiApi.accounts.getBuilderCredits({
        params: { path: { account_id: String(account.id) } },
        signal,
      }),
    queryConfig: { enabled: hasWallet },
  });

  // Scheduled grants (`starts_at` in the future) are not spendable yet, so they must not count
  // toward the balance that feeds the $1,000 top-up cap — counting them blocks a purchase the
  // account is entitled to make.
  const nowSec = Math.floor(Date.now() / 1000);
  const grants = hasWallet ? (data?.grants ?? []) : [];
  const balanceCents = toCents(spendableGrantTotal(grants, nowSec));
  const totalCents = toCents(spendableGrantOriginalTotal(grants, nowSec));
  // Clamp: a remaining balance above its original (never expected) must not report negative use.
  const usedCents = Math.max(0, totalCents - balanceCents);

  // Only paid grants are top-ups. Services puts the Flex plan's monthly send allowance in the same
  // wallet as `tier: 'free'`. Expired top-ups stay in, so an account with no funds left is still funded.
  const topUps = grants.filter((grant) => grant.tier === 'paid' && hasGrantStarted(grant, nowSec));
  const latestGrant = topUps.reduce<(typeof grants)[number] | undefined>(
    (latest, grant) => (latest == null || grant.created_at > latest.created_at ? grant : latest),
    undefined,
  );
  const topUpBalanceCents = toCents(spendableGrantTotal(topUps, nowSec));
  // Funds from earlier top-ups can make the remainder larger than the last top-up. The bar must not show more than 100%.
  const lastTopUpCents = Math.max(topUpBalanceCents, latestGrant == null ? 0 : toCents(latestGrant.credits_original));
  const hasFunded = topUps.length > 0;

  return {
    balanceCents,
    topUpBalanceCents,
    totalCents,
    usedCents,
    lastTopUpCents,
    hasFunded,
    isBuilder,
    isLoading,
    isError,
  };
}
